import { useState, useEffect, useCallback } from 'react';
import {
  fetchWeeklyProgressWeeks, fetchWeeklyProgress, fetchPropertyWeeklyHistory,
  createWeeklyProgress, updateWeeklyProgress, batchCreateWeeklyProgress,
  fetchProperties, fetchWorkCatalog,
} from '@/lib/api';
import { formatMXNCompact, formatDate } from '@/lib/formatters';
import { useAuth } from '@/lib/AuthContext';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Calendar, ChevronLeft, TrendingUp, HardHat, Home, CheckCircle2, AlertTriangle, Clock,
} from 'lucide-react';

export default function AvanceSemanal() {
  const { can } = useAuth();
  const canEdit = can('avance_obra', 'edit');
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [propertyDetail, setPropertyDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const loadWeeks = useCallback(async () => {
    try { setWeeks(await fetchWeeklyProgressWeeks()); }
    catch { toast.error('Error al cargar semanas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadWeeks(); }, [loadWeeks]);

  const openWeek = async (wl) => {
    try {
      setSelectedWeek(wl);
      setWeekData(await fetchWeeklyProgress(wl));
    } catch { toast.error('Error'); }
  };

  const openPropertyHistory = async (pid) => {
    try { setPropertyDetail(await fetchPropertyWeeklyHistory(pid)); }
    catch { toast.error('Error'); }
  };

  // Property detail view
  if (propertyDetail) {
    return (
      <PropertyHistoryView
        data={propertyDetail}
        onClose={() => setPropertyDetail(null)}
      />
    );
  }

  // Week detail view
  if (weekData) {
    return (
      <WeekDetailView
        data={weekData}
        onClose={() => { setWeekData(null); setSelectedWeek(null); loadWeeks(); }}
        onPropertyClick={openPropertyHistory}
        canEdit={canEdit}
        onRefresh={() => openWeek(selectedWeek)}
      />
    );
  }

  // Weeks list
  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-[400px] rounded-xl" /></div>;

  return (
    <div className="space-y-6" data-testid="avance-semanal-page">
      <PageHeader title="Avance Semanal" subtitle={`${weeks.length} semanas registradas`}>
        {canEdit && <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Nueva Semana</Button>}
      </PageHeader>

      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 text-slate-400 shrink-0" />
        <p className="text-[10px] text-slate-500 leading-relaxed">
          <strong>Módulo independiente del flujo financiero.</strong> Avance registrado no significa pago. Avance aprobado no genera egresos. Este registro sirve como soporte operativo para las estimaciones semanales de destajo.
        </p>
      </div>

      {weeks.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-16 text-center">
          <Calendar className="h-14 w-14 text-slate-200 mx-auto mb-4" />
          <p className="text-sm font-medium text-slate-600 mb-1">Sin avance semanal registrado</p>
          <p className="text-xs text-slate-400">Crea la primera semana para empezar a capturar avance.</p>
        </CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table>
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Semana</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Periodo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Viviendas</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-center">Registros</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {weeks.map(w => (
                <TableRow key={w.week_label} className="cursor-pointer table-row-hover group" onClick={() => openWeek(w.week_label)}>
                  <TableCell className="py-2.5 px-3 text-[13px] font-semibold">{w.week_label}</TableCell>
                  <TableCell className="py-2.5 px-3 text-[11px] text-slate-500">{formatDate(w.start_date)} — {formatDate(w.end_date)}</TableCell>
                  <TableCell className="py-2.5 px-3 text-center text-[12px] tabular-nums">{w.property_count}</TableCell>
                  <TableCell className="py-2.5 px-3 text-center text-[12px] tabular-nums">{w.entries}</TableCell>
                  <TableCell className="py-1 px-2"><ChevronLeft className="h-3.5 w-3.5 text-slate-300 rotate-180" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {createOpen && <CreateWeekDialog onClose={() => setCreateOpen(false)} onCreated={(wl) => { setCreateOpen(false); openWeek(wl); }} />}
    </div>
  );
}


/* ══════════════════════════════════════════════
   CREATE WEEK DIALOG
   ══════════════════════════════════════════════ */
function CreateWeekDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ week_label: '', start_date: '', end_date: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Nueva Semana de Avance</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Etiqueta de semana</Label><Input value={form.week_label} onChange={e => set('week_label', e.target.value)} placeholder="Semana 17 — Abr 2026" className="mt-1 h-9" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Inicio</Label><Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="mt-1 h-9 text-sm" /></div>
            <div><Label className="text-xs">Fin</Label><Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} className="mt-1 h-9 text-sm" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!form.week_label || !form.start_date} onClick={() => onCreated(form.week_label)}>Crear y Capturar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════
   WEEK DETAIL VIEW
   ══════════════════════════════════════════════ */
function WeekDetailView({ data, onClose, onPropertyClick, canEdit, onRefresh }) {
  const [captureOpen, setCaptureOpen] = useState(false);

  const totalWeekly = data.properties.reduce((s, p) => s + p.total_weighted_weekly, 0);
  const avgCumulative = data.properties.length > 0 ? data.properties.reduce((s, p) => s + p.total_weighted_cumulative, 0) / data.properties.length : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}><ChevronLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{data.week_label}</h1>
          <p className="text-[13px] text-slate-400">{data.total_properties} viviendas · {data.total_entries} registros</p>
        </div>
        {canEdit && <Button onClick={() => setCaptureOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Capturar Avance</Button>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard label="Viviendas" value={data.total_properties} icon={Home} compact />
        <KPICard label="Registros" value={data.total_entries} icon={HardHat} compact />
        <KPICard label="Av. Semanal Pond." value={`${totalWeekly.toFixed(1)}%`} icon={TrendingUp} compact />
        <KPICard label="Av. Acum. Prom." value={`${avgCumulative.toFixed(1)}%`} icon={CheckCircle2} compact />
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 text-slate-400 shrink-0" />
        <p className="text-[10px] text-slate-500 leading-relaxed">
          <strong>Solo registro operativo.</strong> El avance semanal no genera pagos ni egresos. Sirve como soporte para estimaciones de destajo — los pagos se procesan por separado.
        </p>
      </div>

      {data.properties.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-10 text-center">
          <HardHat className="h-10 w-10 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Sin avance capturado esta semana</p>
        </CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table>
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Vivienda</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-center">Conceptos</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-3 text-right">Av. Semanal Pond.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">Av. Acum. Pond.</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.properties.map(p => (
                <TableRow key={p.property_id} className="cursor-pointer table-row-hover group" onClick={() => onPropertyClick(p.property_id)}>
                  <TableCell className="py-2.5 px-3 text-[13px] font-semibold">Lt {p.property_lot} Mz {p.property_block}</TableCell>
                  <TableCell className="py-2.5 px-3 text-center text-[12px] tabular-nums">{p.concepts_count}</TableCell>
                  <TableCell className="py-2.5 px-3 text-right text-[13px] tabular-nums font-bold text-[#F05A28]">{p.total_weighted_weekly.toFixed(1)}%</TableCell>
                  <TableCell className="py-2.5 px-3 text-right text-[12px] tabular-nums">{p.total_weighted_cumulative.toFixed(1)}%</TableCell>
                  <TableCell className="py-1 px-2"><ChevronLeft className="h-3.5 w-3.5 text-slate-300 rotate-180" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {captureOpen && (
        <CaptureProgressDialog
          weekLabel={data.week_label}
          onClose={() => setCaptureOpen(false)}
          onSaved={() => { setCaptureOpen(false); onRefresh(); }}
        />
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════
   CAPTURE PROGRESS DIALOG
   ══════════════════════════════════════════════ */
function CaptureProgressDialog({ weekLabel, onClose, onSaved }) {
  const [properties, setProperties] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedProp, setSelectedProp] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [currentProgress, setCurrentProgress] = useState('');
  const [notes, setNotes] = useState('');
  const [weekDates, setWeekDates] = useState({ start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([fetchProperties(), fetchWorkCatalog(null, true)])
      .then(([p, c]) => { setProperties(p); setCatalog(c); })
      .catch(() => {});
    // Try to get dates from the week label context
    fetchWeeklyProgressWeeks().then(weeks => {
      const w = weeks.find(wk => wk.week_label === weekLabel);
      if (w) setWeekDates({ start_date: w.start_date, end_date: w.end_date });
    }).catch(() => {});
  }, [weekLabel]);

  const handleSave = async () => {
    if (!selectedProp || !selectedItem || !currentProgress) return;
    setSaving(true);
    try {
      await createWeeklyProgress({
        week_label: weekLabel,
        start_date: weekDates.start_date,
        end_date: weekDates.end_date,
        property_id: selectedProp,
        work_catalog_item_id: selectedItem,
        current_progress_percent: parseFloat(currentProgress),
        notes: notes || null,
      });
      toast.success('Avance registrado');
      setCurrentProgress('');
      setNotes('');
      setSelectedItem('');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // Group catalog by category
  const seen = new Set();
  const categories = catalog.reduce((arr, c) => {
    if (!seen.has(c.category_name)) { seen.add(c.category_name); arr.push(c.category_name); }
    return arr;
  }, []);

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-[#F05A28]" /> Capturar Avance — {weekLabel}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Vivienda</Label>
            <Select value={selectedProp} onValueChange={setSelectedProp}>
              <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleccionar vivienda" /></SelectTrigger>
              <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>Lt {p.lot} Mz {p.block} — {p.name || p.modelo}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Concepto del Catálogo</Label>
            <Select value={selectedItem} onValueChange={setSelectedItem}>
              <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleccionar concepto" /></SelectTrigger>
              <SelectContent>
                {catalog.map(c => <SelectItem key={c.id} value={c.id}>{c.category_name} — {c.concept_name} ({c.weight_percent}%)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Avance Acumulado Actual (%)</Label><Input type="number" min="0" max="100" step="0.1" value={currentProgress} onChange={e => setCurrentProgress(e.target.value)} placeholder="0 - 100" className="mt-1 h-9 text-sm" /></div>
          <div><Label className="text-xs">Observaciones</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Opcional" className="mt-1 h-9 text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={handleSave} disabled={saving || !selectedProp || !selectedItem || !currentProgress}>{saving ? 'Guardando...' : 'Registrar Avance'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════
   PROPERTY HISTORY VIEW
   ══════════════════════════════════════════════ */
function PropertyHistoryView({ data, onClose }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}><ChevronLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Lt {data.property_lot} Mz {data.property_block}</h1>
          <p className="text-[13px] text-slate-400">{data.property_name} · {data.weeks.length} semanas registradas</p>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 flex items-center gap-3">
        <Clock className="h-4 w-4 text-slate-400 shrink-0" />
        <p className="text-[10px] text-slate-500 leading-relaxed">
          Historial de avance operativo. No vinculado a pagos ni egresos — solo referencia para estimaciones.
        </p>
      </div>

      {data.weeks.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-10 text-center"><Calendar className="h-10 w-10 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Sin historial semanal</p></CardContent></Card>
      ) : (
        data.weeks.map(w => (
          <Card key={w.week_label} className="rounded-xl border bg-white overflow-clip">
            <div className="px-4 py-3 border-b bg-slate-50/50 flex items-center justify-between">
              <div>
                <p className="text-[13px] font-semibold text-slate-800">{w.week_label}</p>
                <p className="text-[10px] text-slate-400">{formatDate(w.start_date)} — {formatDate(w.end_date)}</p>
              </div>
              <div className="flex items-center gap-4 text-right">
                <div><p className="text-[9px] uppercase text-[#F05A28]">Semanal</p><p className="text-[14px] font-bold tabular-nums text-[#F05A28]">{w.total_weighted_weekly.toFixed(1)}%</p></div>
                <div><p className="text-[9px] uppercase text-slate-400">Acumulado</p><p className="text-[14px] font-bold tabular-nums text-slate-700">{w.total_weighted_cumulative.toFixed(1)}%</p></div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/30">
                  <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3">Partida / Concepto</TableHead>
                  <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3 text-right w-14">Peso</TableHead>
                  <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3 text-right w-16">Ant.</TableHead>
                  <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3 text-right w-16">Act.</TableHead>
                  <TableHead className="text-[9px] uppercase text-[#F05A28] font-semibold py-2 px-3 text-right w-16">Sem.</TableHead>
                  <TableHead className="text-[9px] uppercase text-slate-400 font-semibold py-2 px-3">Obs.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {w.concepts.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="py-1.5 px-3 text-[11px]">{c.category_name} — {c.concept_name}</TableCell>
                    <TableCell className="py-1.5 px-3 text-right text-[10px] tabular-nums text-slate-400">{c.weight_percent}%</TableCell>
                    <TableCell className="py-1.5 px-3 text-right text-[11px] tabular-nums">{c.previous_progress_percent}%</TableCell>
                    <TableCell className="py-1.5 px-3 text-right text-[11px] tabular-nums font-medium">{c.current_progress_percent}%</TableCell>
                    <TableCell className="py-1.5 px-3 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{c.weekly_progress_percent}%</TableCell>
                    <TableCell className="py-1.5 px-3 text-[10px] text-slate-400 truncate max-w-[120px]">{c.notes || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ))
      )}
    </div>
  );
}
